<!--@format-->
<template>
    <div class="opportunities-manage-stages-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>Manage Stages</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <div v-if="isLoading" class="loading-card">
                    <loading-svg />
                </div>
                <opportunity-stages
                    v-else
                    :clientId="clientId"
                    :opportunityStatuses="opportunityStatuses"
                    @order:update="handleDrag($event)"
                    @new:status="addNewStatus($event)"
                />
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="transparent-btn" @click="showCancelPopup">
                        Cancel
                    </app-button>
                </div>

                <div class="action-container">
                    <app-button :loading="saving" @click="save($event)"> Save</app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const OpportunityStages = () => import('@/components/opportunity/opportunity-stages');
const LoadingSvg = () => import('@/components/svg/loading-screen-svg');

import EventBus from '@/scripts/event-bus.js';
import AppPopup from '@/components/popups/app-popup';

export default {
    name: 'opportunities-manage-stages-widget',

    components: {
        'app-button': AppButton,
        'widget-close-svg': WidgetCloseSvg,
        'opportunity-stages': OpportunityStages,
        'loading-svg': LoadingSvg,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            opportunityStatuses: [],
            isLoading: false,
            saving: false,
        };
    },

    computed: {
        clientId() {
            return this.widget.options.clientId;
        },
    },

    created() {
        this.getOpportunityStatuses();
    },

    beforeDestroy() {},

    methods: {
        showCancelPopup() {
            this.actions = {
                confirm: {
                    label: `Discard Changes and Leave`,
                    action: () => {
                        this.closeWidget();
                    },
                },

                cancel: {
                    label: 'Stay',
                },
            };

            this.$modal.show(
                AppPopup,
                {
                    type: 'alert-popup',
                    title: `Are you sure you want to leave?`,
                    content: `<p>There are unsaved changes. If you leave now, they will be lost. Do you want to proceed with leaving?</p>`,
                    actions: this.actions,
                },
                {
                    height: this.$helpers.setPopupHeight(),
                    width: this.$helpers.setPopupWidth(),
                },
            );
        },

        save() {
            this.saving = true;
            this.$api
                .put(`/api/DealStatuses/${this.clientId}`, this.opportunityStatuses)
                .then(() => {
                    EventBus.$emit('opportunity-stages:update');
                    EventBus.$emit('opportunity:update');
                    this.closeWidget();
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while updating your opportunity stages.',
                        { error },
                    );
                })
                .finally(() => (this.saving = false));
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        getOpportunityStatuses() {
            this.isLoading = true;

            this.$api
                .get(
                    `/service/DealStatuses/?$filter=ClientId eq ${this.clientId}&$orderby=Order asc,CreatedAt asc`,
                )
                .then((response) => {
                    this.setOpportunityStatuses(response.data.value);
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while getting your opportunity stages',
                        { error },
                    );
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        setOpportunityStatuses(stages) {
            var index = 0;

            var inProgressStages = stages.filter((s) => s.IsWon === null);
            var wonStages = stages.filter((s) => s.IsWon === true);
            var lostStages = stages.filter((s) => s.IsWon === false);

            var reorderedStages = [...inProgressStages, ...wonStages, ...lostStages];

            this.opportunityStatuses = reorderedStages.map((stage) => {
                index++;

                return {
                    id: stage.Id,
                    name: stage.Name,
                    color: stage.Color,
                    colorClass: this.$helpers.getOpportunityBackgroundClass(stage.Color),
                    isWon: stage.IsWon,
                    isEditShown: false,
                    isUpdating: false,
                    isDeleting: false,
                    order: index,
                    probability: stage.Probability || 0,
                    status: null,
                    editStatus: 'edit',
                };
            });
        },

        handleDrag(items) {
            items.forEach((item) => {
                var targetStatus = this.opportunityStatuses.find((status) => status.id === item.id);
                var itemIndex = items.indexOf(item) + 1;

                if (targetStatus.order !== itemIndex) {
                    targetStatus.order = itemIndex;
                }
            });
        },

        addNewStatus(newStatus) {
            newStatus.editStatus == 'new';
            this.opportunityStatuses.push(newStatus);
        },
    },
};
</script>

<style>
.opportunities-manage-stages-widget .flex-wrapper {
    display: flex;
    align-items: flex-end;
}

.opportunities-manage-stages-widget .content-wrapper .add-opportunity-status-wrapper {
    margin-bottom: 25px;
    padding: 25px 15px 15px 15px;
    border: 1px solid #cac6cb;
    border-radius: 20px;
    position: relative;
}

.opportunities-manage-stages-widget .content-wrapper .add-opportunity-status-wrapper .upper-text {
    position: absolute;
    top: -10px;
    left: 238px;
    background: #ffffff;
    padding: 0px 15px;
}

.opportunities-manage-stages-widget .content-wrapper .add-opportunity-status-color-wrapper label {
    font: 500 16px/1em 'Roboto';
    margin-bottom: 8px;
}

.opportunities-manage-stages-widget .content-wrapper .add-opportunity-status-wrapper .app-input {
    width: 100%;
    margin-right: 5px;
}

.opportunities-manage-stages-widget .content-wrapper .options {
    display: flex;
    margin: 0px -10px 15px;
}

.opportunities-manage-stages-widget .content-wrapper .options .option {
    margin: 0 12px;
}

.opportunities-manage-stages-widget .content-wrapper .actions {
    display: flex;
    justify-content: center;
    margin-top: 10px;
    width: 100%;
}

.opportunities-manage-stages-widget .content-wrapper .actions .transparent {
    border: 1px solid #cac6cb;
    border-radius: 20px;
    background: transparent;
    color: #382153;
}

.opportunities-manage-stages-widget .content-wrapper .actions .btn {
    margin: 0 4px;
    width: 88px;
    height: 35px;
}

.opportunities-manage-stages-widget .app-input input {
    height: 40px;
}

.opportunities-manage-stages-widget .action-container .btn {
    width: 113px;
}

.opportunities-manage-stages-widget .flex-wrapper {
    width: 100%;
}

.opportunities-manage-stages-widget .percentage {
    width: 160px;
    margin-top: 24px;
}

.opportunities-manage-stages-widget .percentage p {
    font: 500 16px/24px Roboto;
    letter-spacing: 0px;
    color: #382153;
    margin-bottom: 8px;
}

.opportunities-manage-stages-widget .percentage .input {
    display: flex;
    align-items: center;
    font-weight: 500;
}

.add-opportunity-statuses-buttons-wrapper .o6-add-svg path {
    fill: #ffffff;
}

.add-opportunity-statuses-buttons-wrapper .o6-add-svg {
    margin-left: 10px;
}

.opportunities-manage-stages-widget.widget .content-wrapper {
    padding: 25px 30px;
}

.opportunities-manage-stages-widget .cancel-container button {
    background: transparent;
    border: 1px solid #cac6cb;
    color: #382153;
}

.opportunities-manage-stages-widget .cancel-container button:hover {
    background: #e1e0e5;
}
</style>
